import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import translations from '../utils/language/translations.json';
import { useTranslation } from 'react-i18next';

const FeedbackSystem = ({
  concernedUserId,
  FromUserId,
  transactionId,
  liveCsrfToken,
  handleMarkedAsReceived,
  type = 'Seller', // 'Seller' | 'Buyer'
}) => {
  const { i18n } = useTranslation();
  const t = (key) => translations.FeedbackSystem[key]?.[i18n.language] ?? key;

  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const canEdit = !submitted && !loading;

  const onKeyStar = (e, star) => {
    if (!canEdit) return;
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setRating(star); }
    if (e.key === 'ArrowRight') { e.preventDefault(); setRating((r) => Math.min(5, (r || star) + 1)); }
    if (e.key === 'ArrowLeft')  { e.preventDefault(); setRating((r) => Math.max(1, (r || star) - 1)); }
  };

  const handleSubmitFeedback = async () => {
    if (rating < 1) {
      toast.warn(t('Please provide your rating'));
      return;
    }
    setLoading(true);
    try {
      const resp = await axios.post(
        `${process.env.REACT_APP_API_BASE}/api/feedback/Submit`,
        {
          transactionId,
          FromUserId,
          ToUserId: concernedUserId,
          Comment: comment.trim(),
          Rating: rating,
          Type: type,
        },
        {
          headers: { 'x-csrf-token': liveCsrfToken },
          withCredentials: true,
        }
      );
      if (resp.data?.success) {
        toast.success(t('Feedback Submitted Successfully'));
        setSubmitted(true);
      } else {
        toast.error(t('Failed to submit Feedback'));
      }
    } catch (err) {
      toast.error(t('Error submitting Feedback'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="feedback-system-container">
      <h2>{t('Feedback System')}</h2>
      <p>{t('Please provide your rating')}</p>

      {/* Accessible star rating */}
      <div
        className="star-rating"
        role="radiogroup"
        aria-label={t('Rating')}
      >
        {[1,2,3,4,5].map((star) => {
          const active = (hover || rating) >= star;
          return (
            <span
              key={star}
              role="radio"
              aria-checked={rating === star}
              tabIndex={canEdit ? 0 : -1}
              className={`star ${active ? 'selected' : ''}`}
              onMouseEnter={() => canEdit && setHover(star)}
              onMouseLeave={() => canEdit && setHover(0)}
              onClick={() => canEdit && setRating(star)}
              onKeyDown={(e) => onKeyStar(e, star)}
              aria-label={`${star} ${t('stars')}`}
            >
              ★
            </span>
          );
        })}
      </div>

      <div className="feedback-textarea">
        <label>{t('Feedback')}</label>
        <textarea
          value={comment}
          onChange={(e) => canEdit && setComment(e.target.value)}
          maxLength={254}
          rows={4}
          placeholder={t('Feedback placeholder') /* add to translations */}
          disabled={!canEdit}
        />
      </div>

      <div className="button-group">
        {!submitted && (
          <button
            onClick={handleSubmitFeedback}
            className="btn-primary"
            disabled={loading}
          >
            {loading ? t('Submitting…') : t('Submit Feedback')}
          </button>
        )}
        <button
          onClick={handleMarkedAsReceived}
          className="btn-ghost"
          disabled={loading}
        >
          {t('Mark as Received')}
        </button>
      </div>
    </div>
  );
};

export default FeedbackSystem;
